import { AsYouType, getCountries, getCountryCallingCode, parsePhoneNumber } from 'libphonenumber-js';
import type { CountryCode } from 'libphonenumber-js';
import metadata from 'libphonenumber-js/metadata.min.json';

import { SUPPORTED_COUNTRIES } from '@klareospace/pharos-lib-types';
import type { PhoneNumber } from '@klareospace/pharos-lib-types';

/**
 * Converts an ISO 3166-1 alpha-2 country code (e.g. 'FR') to its flag emoji (e.g. '🇫🇷').
 * Uses Regional Indicator Symbol Letters (U+1F1E6–U+1F1FF).
 */
export function countryCodeToFlag(code: string): string {
  return [...code.toUpperCase()].map((c) => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65)).join('');
}

/**
 * Resolves the dial code (calling code) for a given ISO 3166-1 alpha-2 country code.
 * Returns an empty string if the country code is invalid or unsupported by libphonenumber-js.
 */
export function resolveDialCode(country: string): string {
  try {
    return `+${getCountryCallingCode(country as Parameters<typeof getCountryCallingCode>[0])}`;
  } catch {
    return '';
  }
}

/**
 * Performs a reverse lookup from a dial code to the country it belongs to, across every region
 * known to libphonenumber-js (not just SUPPORTED_COUNTRIES). When a calling code is shared by
 * several countries (e.g. +1, +44), resolves to the main country for that code, following the
 * ordering declared in libphonenumber-js's own metadata (e.g. +1 → US, +44 → GB). This is a
 * display/default-selection helper only — actual phone validation (`isBlankOrValidPhone` in
 * `./phone-validation`) never assumes the main country for a shared calling code.
 * Falls back to SUPPORTED_COUNTRIES[0] (France) for an empty or unknown dial code.
 */
export function countryFromDialCode(dialCode: string): CountryCode {
  const callingCode = dialCode.replace('+', '');
  const countriesForCode = metadata.country_calling_codes[callingCode];
  return countriesForCode?.[0] ?? SUPPORTED_COUNTRIES[0];
}

/** A country option enriched with flag emoji, dial code, and display name for use in UI dropdowns. */
export interface CountryOption {
  code: CountryCode;
  dialCode: string;
  displayName: string;
  flag: string;
}

/**
 * Returns an array of country option objects derived from SUPPORTED_COUNTRIES,
 * enriched with flag emoji, dial code, and display name for use in UI dropdowns.
 */
export function getSupportedCountryOptions(locale = 'en'): CountryOption[] {
  return SUPPORTED_COUNTRIES.map((code) => buildCountryOption(code, locale));
}

/**
 * Returns a country option for every region known to libphonenumber-js, sorted by display name.
 * Unlike `getSupportedCountryOptions`, this is not limited to `SUPPORTED_COUNTRIES` — it powers
 * the full dial-code picker used by `PhoneInput`. `locale` (e.g. `'fr'`, `'ar'`) controls both the
 * display name language and the sort order; defaults to `'en'` to keep existing callers unchanged.
 */
export function getAllCountryOptions(locale = 'en'): CountryOption[] {
  return getCountries()
    .map((code) => buildCountryOption(code, locale))
    .sort((a, b) => a.displayName.localeCompare(b.displayName, locale));
}

/**
 * Returns the formatted national representation of an E.164 number string,
 * or null when the number cannot be parsed (incomplete / invalid).
 */
function getNationalFormatted(e164: string, country: CountryCode): string | null {
  try {
    return parsePhoneNumber(e164, country).formatNational();
  } catch {
    const formatter = new AsYouType(country);
    formatter.input(e164);
    return formatter.getNumber()?.formatNational() ?? null;
  }
}

/**
 * Strips any trunk prefix that `formatNational()` added to `nationalFormatted`
 * but that was not part of the original stored `number`.
 *
 * Example: `nationalFormatted = '06 12 34 56 78'`, `number = '612345678'`
 * → returns `'6 12 34 56 78'`
 *
 * Exported for reuse by `./phone-validation`'s `getExampleNationalNumber`.
 */
export function stripTrunkPrefix(nationalFormatted: string, number: string): string {
  const formattedDigits = nationalFormatted.replace(/\D/g, '');
  if (formattedDigits === number || !formattedDigits.endsWith(number)) {
    return nationalFormatted;
  }
  // Skip trunk digit(s) then any separator before the subscriber digits start
  const trunkDigitCount = formattedDigits.length - number.length;
  let digitsSkipped = 0;
  let charIdx = 0;
  while (charIdx < nationalFormatted.length && digitsSkipped < trunkDigitCount) {
    if (/\d/.test(nationalFormatted[charIdx])) digitsSkipped++;
    charIdx++;
  }
  while (charIdx < nationalFormatted.length && !/\d/.test(nationalFormatted[charIdx])) {
    charIdx++;
  }
  return nationalFormatted.slice(charIdx);
}

/**
 * Formats a digits-only national phone number with country-appropriate spacing.
 * The `number` argument is the subscriber number as stored in `PhoneNumber.number`
 * (i.e. without the country's trunk/national prefix, e.g. `'612345678'` for a
 * French mobile).
 *
 * Examples:
 *   - FR `'612345678'` → `'6 12 34 56 78'`
 *   - LB `'3123456'`   → `'3 123 456'`
 *
 * Pure and defensive: returns the raw `number` on any throw.
 */
export function formatNationalNumber(number: string, country: CountryCode): string {
  if (!number) return number;
  try {
    const e164 = `+${getCountryCallingCode(country)}${number}`;
    const nationalFormatted = getNationalFormatted(e164, country);
    if (!nationalFormatted) return number;
    return stripTrunkPrefix(nationalFormatted, number);
  } catch {
    return number;
  }
}

/**
 * Strips the national trunk prefix (if any) from a raw digit string and returns the
 * national significant number for the given country.
 *
 * `PhoneNumber.number` must be the national significant number — the subscriber
 * portion without the country's trunk/access prefix (e.g. the leading '0' in France).
 * This helper normalises whatever the user typed so callers can store a consistent value.
 *
 * Behaviour:
 *   - Non-digit characters are stripped before processing.
 *   - Returns `''` for empty input.
 *   - Falls back to the cleaned digit string when libphonenumber-js cannot resolve a
 *     national number (e.g. incomplete input).
 *   - Idempotent: already-normalised numbers are returned unchanged.
 *
 * Examples:
 *   - FR `'0612345678'` → `'612345678'`
 *   - FR `'612345678'`  → `'612345678'`
 *   - `''`             → `''`
 */
export function toNationalNumber(input: string, country: CountryCode): string {
  const cleaned = input.replace(/\D/g, '');
  if (!cleaned) return '';
  try {
    const formatter = new AsYouType(country);
    formatter.input(cleaned);
    return formatter.getNumber()?.nationalNumber ?? cleaned;
  } catch {
    return cleaned;
  }
}

/**
 * Formats a `PhoneNumber` struct to a callable string for use in `tel:` links.
 *
 * Concatenates `dialCode` and `number` without any separator,
 * e.g. `{ dialCode: '+33', number: '612345678' }` → `'+33612345678'`.
 */
export function formatPhoneNumber(phone: PhoneNumber): string {
  return `${phone.dialCode}${phone.number}`;
}

/**
 * Returns a human-readable country name for a given ISO 3166-1 alpha-2 country code, in the given
 * `locale` (e.g. `'fr'`, `'ar'`). Defaults to `'en'` so existing callers (mobile included) keep
 * their current behaviour unchanged.
 * Uses the Intl.DisplayNames API (available in all modern browsers and Hermes >= iOS 13 / Android 5).
 * Falls back to the raw country code if Intl.DisplayNames is unavailable.
 */
export function getCountryDisplayName(code: string, locale = 'en'): string {
  try {
    const displayNames = new Intl.DisplayNames([locale], { type: 'region' });
    return displayNames.of(code) ?? code;
  } catch {
    return code;
  }
}

// ─── Helpers ─────────────────────────────────────────────────────────────────────

function buildCountryOption(code: CountryCode, locale: string): CountryOption {
  return {
    code,
    dialCode: resolveDialCode(code),
    displayName: getCountryDisplayName(code, locale),
    flag: countryCodeToFlag(code),
  };
}
